import React, { useCallback, useRef, useState } from "react";
import { Form, Input, Select, Tag, Tooltip, Space, Typography, Alert } from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  RobotOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Button, Modal, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";
import useAccess from "../../hooks/useAccess";

const { Text } = Typography;

const STATUS_TAG = {
  Draft: { color: "default", label: "Draft" },
  Published: { color: "success", label: "Published" },
  Archived: { color: "warning", label: "Archived" },
};

/**
 * The client's agent workspace. The list mixes two kinds of row: agents published
 * platform-wide (no companyId — read-only, shown as "Shared") and the client's own,
 * which they can edit, publish and issue keys for.
 */
const PortalAgents = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [llmModels, setLlmModels] = useState([]);
  const { mutate, loading } = useMutations();
  const { confirmAction } = useConfirm();
  const { companyId } = useAccess();
  const tableRef = useRef(null);

  const isMine = (record) => record.companyId != null;

  const fetchData = useCallback(
    async (page, pageSize, search) => {
      let result = { data: [], total: 0 };
      await mutate("portalAgents", {
        method: "get",
        endpoint: "/portal/agents",
        params: { page, pageSize, search },
        onSuccess: (res) => {
          result = { data: res?.items || [], total: res?.totalCount || 0 };
        },
        onError: () => {
          result = { data: [], total: 0 };
        },
      });
      return result;
    },
    [mutate]
  );

  const loadModels = useCallback(
    async (provider) => {
      await mutate("portalLlmModels", {
        method: "get",
        endpoint: "/portal/llm-models",
        params: provider ? { provider } : {},
        onSuccess: (res) => setLlmModels(res || []),
        onError: () => setLlmModels([]),
      });
    },
    [mutate]
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          <RobotOutlined style={{ color: "#1677ff" }} />
          <Button
            variant="ghost"
            style={{ padding: 0, height: "auto" }}
            disabled={!isMine(record)}
            onClick={() => navigate(`/portal/agents/${record.agentId}`)}
          >
            {text}
          </Button>
          {!isMine(record) && (
            <Tooltip title="Published by Kubit AI for all clients — available to use, not editable">
              <Tag icon={<GlobalOutlined />} color="geekblue">
                Shared
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (t) => t || <Text type="secondary">—</Text>,
      responsive: ["md"],
    },
    { title: "Provider", dataIndex: "provider", key: "provider" },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      render: (m) => m || <Text type="secondary">—</Text>,
      responsive: ["lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const cfg = STATUS_TAG[s] || { color: "default", label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        isMine(record) ? (
          <Space size="small">
            <Tooltip title="Open">
              <Button
                variant="ghost"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/portal/agents/${record.agentId}`)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                variant="ghost"
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  confirmAction({
                    key: "portalDeleteAgent",
                    method: "delete",
                    endpoint: `/portal/agents/${record.agentId}`,
                    title: "Delete agent",
                    content: `Delete "${record.name}"? Its keys, files and version history go with it.`,
                    okText: "Delete",
                    okType: "danger",
                    onSuccess: () => tableRef.current?.reloadPage(),
                  })
                }
              />
            </Tooltip>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  const onCreate = () => {
    form.resetFields();
    form.setFieldsValue({ provider: "OpenAI" });
    loadModels("OpenAI");
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    let newId = null;

    await mutate("portalCreateAgent", {
      method: "post",
      endpoint: "/portal/agents",
      payload: values,
      onSuccess: (res) => {
        newId = res?.agentId;
      },
    });

    setOpen(false);
    if (newId) navigate(`/portal/agents/${newId}`);
    else tableRef.current?.reloadPage();
  };

  return (
    <div>
      <PageHeader
        title="My Agents"
        subtitle="Your AI agents — create, configure and monitor them here."
        extra={
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            onClick={onCreate}
            disabled={!companyId}
          >
            New agent
          </Button>
        }
      />

      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="agentId"
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleOk}
        title="Create agent"
        confirmText="Create"
        loading={loading?.portalCreateAgent}
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Agents you create here are private to your organisation."
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. Invoice extractor" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="What does this agent do?" />
          </Form.Item>
          <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
            <Select
              onChange={(p) => {
                form.setFieldValue("model", undefined);
                loadModels(p);
              }}
            >
              <Select.Option value="OpenAI">OpenAI</Select.Option>
              <Select.Option value="Claude">Claude</Select.Option>
              <Select.Option value="Vision">Google Vision</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="model" label="Model">
            <Select
              placeholder="Choose a model"
              options={llmModels.map((m) => ({
                value: m.modelId,
                label: `${m.displayName} — ${m.modelId}`,
              }))}
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>
          <Form.Item name="systemPrompt" label="System prompt (training)">
            <Input.TextArea
              rows={6}
              placeholder="Instructions describing the agent's behaviour, tone and constraints…"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PortalAgents;
